import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useTCoins } from '../../../context/TCoinsContext';
import { EQUIPMENT_TYPES } from '../../../data/nurseAppModels';
import { CATALOG_PRICING } from '../../../data/defaultCatalogs';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const CatalogManagementPage = () => {
  const navigate = useNavigate();
  const { catalogs, loading } = useEquipment();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { getTotalTCoins, deductTCoins } = useTCoins();
  
  // Check user plan (for now, default to FREE - can be extended later)
  const userPlan = currentUser?.plan || 'FREE';
  const totalTCoins = getTotalTCoins();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    category: '',
    subCategory: '',
    type: EQUIPMENT_TYPES.SMALL_ITEM,
    size: '',
    unit: '',
    color: '',
    brand: '',
    model: '',
    minStock: '',
    maxStock: '',
    reorderPoint: '',
    defaultLocation: '',
    defaultDepartment: '',
    defaultWard: '',
    useBarcode: false,
    barcodePrefix: '',
    autoGenerateBarcode: false,
    tags: [],
    notes: ''
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  // Predefined options for dropdowns
  const categoryOptions = [
    'อุปกรณ์ทางการแพทย์',
    'อุปกรณ์ดูแลผู้ป่วย',
    'อุปกรณ์ฉุกเฉิน',
    'อุปกรณ์ทั่วไป',
    'อุปกรณ์ห้องผ่าตัด',
    'อุปกรณ์ห้องคลอด'
  ];

  const subCategoryOptions = {
    'อุปกรณ์ทางการแพทย์': [
      'อุปกรณ์ให้สารน้ำ',
      'อุปกรณ์ช่วยหายใจ',
      'อุปกรณ์วัดสัญญาณชีพ',
      'อุปกรณ์ฉีดยา',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ดูแลผู้ป่วย': [
      'อุปกรณ์ทำความสะอาด',
      'อุปกรณ์จัดเก็บ',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ฉุกเฉิน': [
      'อุปกรณ์ช่วยชีวิต',
      'อุปกรณ์ปฐมพยาบาล',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ทั่วไป': [
      'อุปกรณ์สำนักงาน',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ห้องผ่าตัด': [
      'อุปกรณ์ผ่าตัด',
      'อุปกรณ์อื่นๆ'
    ],
    'อุปกรณ์ห้องคลอด': [
      'อุปกรณ์คลอด',
      'อุปกรณ์อื่นๆ'
    ]
  };

  const unitOptions = [
    'เส้น',
    'ชิ้น',
    'กล่อง',
    'เครื่อง',
    'ม้วน',
    'คู่',
    'ชุด',
    'ขวด',
    'กระป๋อง',
    'ถุง',
    'แผ่น',
    'อัน',
    'ตัว',
    'อัน',
    'เล่ม'
  ];

  const colorOptions = [
    'ใส',
    'ขาว',
    'ฟ้า',
    'เขียว',
    'เหลือง',
    'แดง',
    'ชมพู',
    'ม่วง',
    'ดำ',
    'เทา',
    'อื่นๆ'
  ];

  const locationOptions = [
    'ตู้เก็บอุปกรณ์ 1',
    'ตู้เก็บอุปกรณ์ 2',
    'ตู้เก็บอุปกรณ์ 3',
    'ห้องเก็บอุปกรณ์',
    'ห้อง ICU',
    'ห้องผ่าตัด',
    'ห้องคลอด',
    'ห้องฉุกเฉิน',
    'อื่นๆ'
  ];

  const departmentOptions = [
    'อายุรกรรม',
    'ศัลยกรรม',
    'สูติ-นรีเวช',
    'กุมารเวช',
    'ICU',
    'ห้องผ่าตัด',
    'ห้องคลอด',
    'ห้องฉุกเฉิน',
    'แผนกอื่นๆ'
  ];

  const wardOptions = [
    'หอผู้ป่วยอายุรกรรม',
    'หอผู้ป่วยศัลยกรรม',
    'หอผู้ป่วยสูติ-นรีเวช',
    'หอผู้ป่วยกุมารเวช',
    'หอผู้ป่วยวิกฤต (ICU)',
    'หอผู้ป่วยอื่นๆ'
  ];

  // Check if user is Leader
  const isLeader = currentTeam?.userRole === 'LEADER';
  
  // Permission checks
  const canCreateCatalog = () => {
    if (!isLeader) return false;
    if (userPlan === 'PRO') return true;
    if (totalTCoins >= CATALOG_PRICING.CREATE_CATALOG) return true;
    return false;
  };
  
  const canEditCatalog = (catalog) => {
    if (!isLeader) return false;
    // Default catalogs can be edited (same as custom catalogs)
    if (userPlan === 'PRO') return true;
    if (totalTCoins >= CATALOG_PRICING.EDIT_CATALOG) return true;
    return false;
  };
  
  const canDeleteCatalog = (catalog) => {
    if (!isLeader) return false;
    // Default catalogs can be deleted (same as custom catalogs)
    if (userPlan === 'PRO') return true;
    if (totalTCoins >= CATALOG_PRICING.DELETE_CATALOG) return true;
    return false;
  };

  // Filter catalogs
  const filteredCatalogs = catalogs.filter(catalog => {
    const matchesSearch = !searchTerm || 
      catalog.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      catalog.code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || catalog.type === filterType;
    const matchesCategory = filterCategory === 'all' || catalog.category === filterCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  // Get unique categories
  const categories = [...new Set(catalogs.map(c => c.category).filter(Boolean))];

  const handleCreate = () => {
    setEditingCatalog(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      category: '',
      subCategory: '',
      type: EQUIPMENT_TYPES.SMALL_ITEM,
      size: '',
      unit: '',
      color: '',
      brand: '',
      model: '',
      minStock: '',
      maxStock: '',
      reorderPoint: '',
      defaultLocation: '',
      defaultDepartment: '',
      defaultWard: '',
      useBarcode: false,
      barcodePrefix: '',
      autoGenerateBarcode: false,
      tags: [],
      notes: ''
    });
    setShowCreateModal(true);
  };

  const handleEdit = (catalog) => {
    setEditingCatalog(catalog);
    setFormData({
      name: catalog.name || '',
      code: catalog.code || '',
      description: catalog.description || '',
      category: catalog.category || '',
      subCategory: catalog.subCategory || '',
      type: catalog.type || EQUIPMENT_TYPES.SMALL_ITEM,
      size: catalog.size || '',
      unit: catalog.unit || '',
      color: catalog.color || '',
      brand: catalog.brand || '',
      model: catalog.model || '',
      minStock: catalog.minStock || '',
      maxStock: catalog.maxStock || '',
      reorderPoint: catalog.reorderPoint || '',
      defaultLocation: catalog.defaultLocation || '',
      defaultDepartment: catalog.defaultDepartment || '',
      defaultWard: catalog.defaultWard || '',
      useBarcode: catalog.useBarcode || false,
      barcodePrefix: catalog.barcodePrefix || '',
      autoGenerateBarcode: catalog.autoGenerateBarcode || false,
      tags: catalog.tags || [],
      notes: catalog.notes || ''
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (catalogId) => {
    const catalog = catalogs.find(c => c.id === catalogId);
    if (!catalog) return;
    
    if (!canDeleteCatalog(catalog)) {
      alert('คุณไม่มีสิทธิ์ลบ Catalog นี้');
      return;
    }
    
    if (!window.confirm('ยืนยันการลบ Catalog?')) return;

    try {
      // Deduct T Coins if not Pro
      if (userPlan !== 'PRO' && catalog.isDefault === false) {
        const deducted = await deductTCoins(currentUser.uid, 'real', CATALOG_PRICING.DELETE_CATALOG, 'ลบ Catalog');
        if (!deducted) {
          alert('ไม่สามารถหัก T Coins ได้');
          return;
        }
      }
      
      await deleteDoc(doc(db, 'equipmentCatalogs', catalogId));
      alert('ลบ Catalog สำเร็จ');
    } catch (err) {
      console.error('Error deleting catalog:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !currentTeam) {
      alert('กรุณาเข้าสู่ระบบและเลือกทีม');
      return;
    }

    if (!isLeader) {
      alert('คุณไม่มีสิทธิ์สร้าง/แก้ไข Catalog');
      return;
    }
    
    // Check permissions
    if (editingCatalog) {
      if (!canEditCatalog(editingCatalog)) {
        alert('คุณไม่มีสิทธิ์แก้ไข Catalog นี้');
        return;
      }
    } else {
      if (!canCreateCatalog()) {
        alert(`คุณต้องมี Pro Plan หรือมี T Coins อย่างน้อย ${CATALOG_PRICING.CREATE_CATALOG} เหรียญ`);
        return;
      }
    }

    try {
      const catalogData = {
        teamId: currentTeam.id,
        name: formData.name,
        code: formData.code,
        description: formData.description || '',
        category: formData.category,
        subCategory: formData.subCategory || null,
        type: formData.type,
        size: formData.size || null,
        unit: formData.unit,
        color: formData.color || null,
        brand: formData.brand || null,
        model: formData.model || null,
        minStock: formData.type === EQUIPMENT_TYPES.SMALL_ITEM ? (formData.minStock ? parseInt(formData.minStock) : null) : null,
        maxStock: formData.type === EQUIPMENT_TYPES.SMALL_ITEM ? (formData.maxStock ? parseInt(formData.maxStock) : null) : null,
        reorderPoint: formData.type === EQUIPMENT_TYPES.SMALL_ITEM ? (formData.reorderPoint ? parseInt(formData.reorderPoint) : null) : null,
        defaultLocation: formData.defaultLocation || null,
        defaultDepartment: formData.defaultDepartment || null,
        defaultWard: formData.defaultWard || null,
        useBarcode: formData.type === EQUIPMENT_TYPES.LARGE_ITEM ? formData.useBarcode : false,
        barcodePrefix: formData.type === EQUIPMENT_TYPES.LARGE_ITEM ? (formData.barcodePrefix || null) : null,
        autoGenerateBarcode: formData.type === EQUIPMENT_TYPES.LARGE_ITEM ? formData.autoGenerateBarcode : false,
        tags: formData.tags || [],
        notes: formData.notes || null,
        status: 'ACTIVE',
        usageCount: editingCatalog?.usageCount || 0,
        updatedAt: serverTimestamp()
      };

      // Deduct T Coins if not Pro
      if (userPlan !== 'PRO') {
        const cost = editingCatalog ? CATALOG_PRICING.EDIT_CATALOG : CATALOG_PRICING.CREATE_CATALOG;
        const deducted = await deductTCoins(currentUser.uid, 'real', cost, editingCatalog ? 'แก้ไข Catalog' : 'สร้าง Catalog');
        if (!deducted) {
          alert('ไม่สามารถหัก T Coins ได้');
          return;
        }
      }
      
      if (editingCatalog) {
        // Update existing
        await updateDoc(doc(db, 'equipmentCatalogs', editingCatalog.id), {
          ...catalogData,
          updatedAt: serverTimestamp()
        });
        alert('แก้ไข Catalog สำเร็จ');
      } else {
        // Create new
        catalogData.createdBy = currentUser.uid;
        catalogData.createdByName = currentUser.displayName || currentUser.email;
        catalogData.isDefault = false; // Mark as custom catalog
        catalogData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'equipmentCatalogs'), catalogData);
        alert('สร้าง Catalog สำเร็จ');
      }

      setShowCreateModal(false);
      setEditingCatalog(null);
    } catch (err) {
      console.error('Error saving catalog:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <button
            onClick={() => navigate('/nurse/teams')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <span>←</span> กลับ
          </button>
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">จัดการ Catalog อุปกรณ์</h1>
            {isLeader && (
              <button
                onClick={handleCreate}
                disabled={!canCreateCatalog()}
                className={`px-4 py-2 rounded-lg transition ${
                  canCreateCatalog()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={!canCreateCatalog() ? `ต้องมี Pro Plan หรือ T Coins อย่างน้อย ${CATALOG_PRICING.CREATE_CATALOG} เหรียญ` : ''}
              >
                + สร้าง Catalog ใหม่
                {userPlan !== 'PRO' && ` (${CATALOG_PRICING.CREATE_CATALOG}🪙)`}
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="text"
              placeholder="🔍 ค้นหา Catalog..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">📦 ประเภท: ทั้งหมด</option>
              <option value={EQUIPMENT_TYPES.SMALL_ITEM}>ชิ้นเล็ก</option>
              <option value={EQUIPMENT_TYPES.LARGE_ITEM}>ชิ้นใหญ่</option>
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">📁 หมวดหมู่: ทั้งหมด</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Catalog List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        ) : filteredCatalogs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">ยังไม่มี Catalog</h3>
            <p className="text-gray-600 mb-4">
              {isLeader 
                ? 'สร้าง Catalog ใหม่เพื่อให้สมาชิกในทีมใช้เมื่อสร้างอุปกรณ์'
                : 'ยังไม่มี Catalog ในทีมนี้'}
            </p>
            {isLeader && (
              <button
                onClick={handleCreate}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                + สร้าง Catalog ใหม่
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCatalogs.map(catalog => (
              <div key={catalog.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-gray-800">{catalog.name}</h3>
                      {catalog.isDefault && (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                          Default
                        </span>
                      )}
                      {!catalog.isDefault && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                          Custom
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{catalog.code}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    catalog.type === EQUIPMENT_TYPES.SMALL_ITEM 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {catalog.type === EQUIPMENT_TYPES.SMALL_ITEM ? 'ชิ้นเล็ก' : 'ชิ้นใหญ่'}
                  </span>
                </div>
                <div className="space-y-2 text-sm mb-4">
                  <p className="text-gray-600">หมวดหมู่: {catalog.category}</p>
                  {catalog.unit && <p className="text-gray-600">หน่วย: {catalog.unit}</p>}
                  {catalog.usageCount > 0 && (
                    <p className="text-gray-500">ใช้แล้ว: {catalog.usageCount} ครั้ง</p>
                  )}
                </div>
                {isLeader && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(catalog)}
                      disabled={!canEditCatalog(catalog)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm transition ${
                        canEditCatalog(catalog)
                          ? 'bg-gray-600 text-white hover:bg-gray-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      title={!canEditCatalog(catalog) ? `ต้องมี Pro Plan หรือ T Coins อย่างน้อย ${CATALOG_PRICING.EDIT_CATALOG} เหรียญ` : ''}
                    >
                      แก้ไข
                      {userPlan !== 'PRO' && ` (${CATALOG_PRICING.EDIT_CATALOG}🪙)`}
                    </button>
                    <button
                      onClick={() => handleDelete(catalog.id)}
                      disabled={!canDeleteCatalog(catalog)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm transition ${
                        canDeleteCatalog(catalog)
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      title={!canDeleteCatalog(catalog) ? `ต้องมี Pro Plan หรือ T Coins อย่างน้อย ${CATALOG_PRICING.DELETE_CATALOG} เหรียญ` : ''}
                    >
                      ลบ
                      {userPlan !== 'PRO' && ` (${CATALOG_PRICING.DELETE_CATALOG}🪙)`}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">
                    {editingCatalog ? 'แก้ไข Catalog' : 'สร้าง Catalog อุปกรณ์'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingCatalog(null);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Basic Info */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">ข้อมูลพื้นฐาน</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ชื่อ Catalog *
                        <span className="ml-2 text-xs text-gray-500 font-normal">
                          💡 ชื่อของแม่แบบอุปกรณ์ (เช่น "เข็มฉีดยา 18G")
                        </span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        รหัส Catalog *
                        <span className="ml-2 text-xs text-gray-500 font-normal">
                          💡 รหัสเฉพาะของแม่แบบ (เช่น "CAT-NEEDLE-18G")
                        </span>
                      </label>
                      <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        คำอธิบาย
                        <span className="ml-2 text-xs text-gray-500 font-normal">
                          💡 รายละเอียดเพิ่มเติมของแม่แบบ
                        </span>
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Category & Type */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">หมวดหมู่</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        หมวดหมู่ *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          category: e.target.value,
                          subCategory: '' // Reset subcategory when category changes
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">-- เลือกหมวดหมู่ --</option>
                        {categoryOptions.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    {formData.category && subCategoryOptions[formData.category] && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          หมวดหมู่ย่อย
                        </label>
                        <select
                          value={formData.subCategory}
                          onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- เลือกหมวดหมู่ย่อย --</option>
                          {subCategoryOptions[formData.category].map(subCat => (
                            <option key={subCat} value={subCat}>{subCat}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ประเภท *
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="type"
                            value={EQUIPMENT_TYPES.SMALL_ITEM}
                            checked={formData.type === EQUIPMENT_TYPES.SMALL_ITEM}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            className="mr-2"
                          />
                          ชิ้นเล็ก (Stock)
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="type"
                            value={EQUIPMENT_TYPES.LARGE_ITEM}
                            checked={formData.type === EQUIPMENT_TYPES.LARGE_ITEM}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            className="mr-2"
                          />
                          ชิ้นใหญ่ (Individual)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Physical Properties */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">คุณสมบัติ</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ขนาด
                      </label>
                      <input
                        type="text"
                        value={formData.size}
                        onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="เช่น 18G, 20G"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        หน่วย *
                      </label>
                      <select
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">-- เลือกหน่วย --</option>
                        {unitOptions.map(unit => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        สี
                      </label>
                      <select
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- เลือกสี --</option>
                        {colorOptions.map(color => (
                          <option key={color} value={color}>{color}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Stock Settings (Small Items) */}
                {formData.type === EQUIPMENT_TYPES.SMALL_ITEM && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">
                      ตั้งค่า Stock เริ่มต้น (ไม่บังคับ)
                      <span className="ml-2 text-xs text-gray-500 font-normal">
                        💡 ค่าเริ่มต้นสำหรับอุปกรณ์ที่สร้างจาก Catalog นี้
                      </span>
                    </h3>
                    <p className="text-xs text-gray-500 mb-4">
                      ถ้าไม่ระบุ จะต้องตั้งค่าเมื่อสร้างอุปกรณ์
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stock ต่ำสุด
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.minStock}
                          onChange={(e) => setFormData({ ...formData, minStock: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stock สูงสุด
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.maxStock}
                          onChange={(e) => setFormData({ ...formData, maxStock: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          จุดสั่งซื้อใหม่
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.reorderPoint}
                          onChange={(e) => setFormData({ ...formData, reorderPoint: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Brand & Model (Large Items) */}
                {formData.type === EQUIPMENT_TYPES.LARGE_ITEM && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-4">ยี่ห้อ/รุ่น</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ยี่ห้อ
                        </label>
                        <input
                          type="text"
                          value={formData.brand}
                          onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          รุ่น
                        </label>
                        <input
                          type="text"
                          value={formData.model}
                          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="mt-4 space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.useBarcode}
                          onChange={(e) => setFormData({ ...formData, useBarcode: e.target.checked })}
                          className="mr-2"
                        />
                        ใช้ Barcode
                      </label>
                      {formData.useBarcode && (
                        <>
                          <input
                            type="text"
                            value={formData.barcodePrefix}
                            onChange={(e) => setFormData({ ...formData, barcodePrefix: e.target.value })}
                            placeholder="Prefix (เช่น IP-)"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.autoGenerateBarcode}
                              onChange={(e) => setFormData({ ...formData, autoGenerateBarcode: e.target.checked })}
                              className="mr-2"
                            />
                            สร้าง Barcode อัตโนมัติ
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Location Defaults */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">ตำแหน่งเริ่มต้น</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ตำแหน่ง
                      </label>
                      <select
                        value={formData.defaultLocation}
                        onChange={(e) => setFormData({ ...formData, defaultLocation: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- เลือกตำแหน่ง --</option>
                        {locationOptions.map(loc => (
                          <option key={loc} value={loc}>{loc}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        แผนก
                      </label>
                      <select
                        value={formData.defaultDepartment}
                        onChange={(e) => setFormData({ ...formData, defaultDepartment: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- เลือกแผนก --</option>
                        {departmentOptions.map(dept => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        หอผู้ป่วย
                      </label>
                      <select
                        value={formData.defaultWard}
                        onChange={(e) => setFormData({ ...formData, defaultWard: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- เลือกหอผู้ป่วย --</option>
                        {wardOptions.map(ward => (
                          <option key={ward} value={ward}>{ward}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    หมายเหตุ
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingCatalog(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingCatalog ? 'บันทึกการแก้ไข' : 'บันทึก'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogManagementPage;

